import { useMemo } from "react";
import type { CustomerCreatePayload } from "@/customTypings/customer/Customer";
import type { HaulierCreatePayload } from "@/customTypings/haulier/Haulier";
import type { ProcessorCreatePayload } from "@/customTypings/processor/Processor";
import type { SiteCreatePayload } from "@/customTypings/site/Site";
import type { TipperCreatePayload } from "@/customTypings/tipper/Tipper";
import type { VehicleCreatePayload } from "@/customTypings/vehicle/Vehicle";
import { createCustomerRestoreConfig } from "./configs/customerRestoreConfig";
import { createHaulierRestoreConfig } from "./configs/haulierRestoreConfig";
import { createProcessorRestoreConfig } from "./configs/processorRestoreConfig";
import {
    createReferenceRestoreConfig,
    type ReferencePendingRestoreForm,
} from "./configs/referenceRestoreConfig";
import { createSiteRestoreConfig } from "./configs/siteRestoreConfig";
import { createTipperRestoreConfig } from "./configs/tipperRestoreConfig";
import { createVehicleRestoreConfig } from "./configs/vehicleRestoreConfig";
import { useRestoreOnCreateConflict } from "./useRestoreOnCreateConflict";

function pickEntityRestoreResult<TPending>(result: ReturnType<typeof useRestoreOnCreateConflict>) {
    return {
        handleCreateError: result.handleCreateError,
        setPendingRestoreForm: result.setPendingRestoreForm as (form: TPending | null) => void,
        confirmRestore: result.confirmRestore,
        restoreModalProps: result.restoreModalProps,
    };
}

export function useCustomerRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: CustomerCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
}) {
    const config = useMemo(
        () => createCustomerRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<CustomerCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useVehicleRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: VehicleCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
}) {
    const config = useMemo(
        () => createVehicleRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<VehicleCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useSiteRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: SiteCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail, siteName: string) => void;
}) {
    const config = useMemo(
        () => createSiteRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<SiteCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useProcessorRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: ProcessorCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
}) {
    const config = useMemo(
        () => createProcessorRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<ProcessorCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useHaulierRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: HaulierCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
}) {
    const config = useMemo(
        () => createHaulierRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<HaulierCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useTipperRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    restoreDeletedWithCreateValues: (payload: TipperCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
}) {
    const config = useMemo(
        () => createTipperRestoreConfig(options),
        [options.restoreDeletedWithCreateValues, options.onRestored]
    );
    return pickEntityRestoreResult<TipperCreatePayload>(useRestoreOnCreateConflict(config));
}

export function useReferenceRestoreOnCreateConflict<TDetail extends { id: string }>(options: {
    deletedRestoreAvailableCode: string;
    entityLabel: string;
    formatSuccessLabel: (code: string | undefined) => string;
    restoreDeletedWithCreateValues: (payload: {
        id: string;
        description: string;
        isActive?: boolean;
    }) => Promise<TDetail>;
    onRestored: (detail: TDetail, code: string) => void;
}) {
    const config = useMemo(
        () => createReferenceRestoreConfig(options),
        [
            options.deletedRestoreAvailableCode,
            options.entityLabel,
            options.formatSuccessLabel,
            options.restoreDeletedWithCreateValues,
            options.onRestored,
        ]
    );
    const result = useRestoreOnCreateConflict(config);
    return {
        restoreConflict: result.restoreConflict,
        pendingRestoreForm: result.pendingRestoreForm as ReferencePendingRestoreForm | null,
        restoringDeleted: result.restoringDeleted,
        handleCreateError: result.handleCreateError,
        setPendingRestoreForm: result.setPendingRestoreForm as (
            form: ReferencePendingRestoreForm | null
        ) => void,
        clearRestoreConflict: result.clearRestoreConflict,
        confirmRestore: result.confirmRestore,
        restoreModalProps: result.restoreModalProps,
    };
}
